// Polls hook used by the public dashboard components.
//
// Strategy: SWR fetches /api/polls in the background. Until the response
// lands, OR if it errors, components see the hardcoded `POLLS` array
// from src/data/surveys.ts so the dashboard never goes blank. Once the
// API responds, components rerender against the live data.
//
// The hook returns the legacy `Poll[]` shape — chart code is unchanged.

import useSWR from 'swr';
import { POLLS as FALLBACK_POLLS } from '../data/surveys';
import type { Poll } from '../data/surveys.types';
import type { PollRow } from '../db/types';
import { rowToPoll } from './dbPollMapper';

interface ApiResponse {
  polls: PollRow[];
  fetchedAt: string;
}

const fetcher = async (url: string): Promise<ApiResponse> => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ApiResponse;
};

export interface UsePollsResult {
  polls: Poll[];
  source: 'live' | 'fallback';
  isLoading: boolean;
  error: Error | null;
}

export function usePolls(): UsePollsResult {
  const { data, error, isLoading } = useSWR<ApiResponse>('/api/polls', fetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 30_000,
    errorRetryCount: 2,
  });

  if (error) {
    if (typeof console !== 'undefined') {
      console.warn('[usePolls] /api/polls failed, using hardcoded fallback:', error);
    }
    return { polls: FALLBACK_POLLS, source: 'fallback', isLoading: false, error };
  }

  if (!data || data.polls.length === 0) {
    return {
      polls: FALLBACK_POLLS,
      source: 'fallback',
      isLoading,
      error: null,
    };
  }

  // Sort oldest-first to match the existing FALLBACK_POLLS convention.
  const polls = [...data.polls]
    .sort((a, b) => a.poll_date.localeCompare(b.poll_date))
    .map(rowToPoll);

  return { polls, source: 'live', isLoading: false, error: null };
}
